import { useState } from 'react';
import { useAppKit, useWalletInfo } from '@reown/appkit/react';
import { Wallet, X } from 'lucide-react';


import { UserStats } from '../UserStats';
import { PointsDisplay } from '../PointsDisplay';
import { WalletDropdown } from '../WalletDropdown';
import { PhantomConnectButton } from '../PhantomConnectButton';
import { SolflareCampaignBadge } from '../SolflareCampaignBadge';
import { MagicEdenCampaignBadge } from '../MagicEdenCampaignBadge';
import { MagicEdenStakingPromotion } from '../MagicEdenStakingPromotion';
import { GateCampaignBadge } from '../GateCampaignBadge';
import { ReferralCampaignBadge } from '../ReferralCampaignBadge';
import { GateWalletConnectButton } from '../GateWalletConnectButton';

import { useIsPhantomActive, usePhantom } from '@/context/PhantomContext';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { cn } from '@/lib/utils';
import config from '@/config';
import {
  isGateWalletInstalled,
  GATE_WALLET_DOWNLOAD_URL,
} from '@/adapters/GateWalletAdapter';

const SOLFLARE_WALLET_ID = '1ca0bdd4747578705b1939af023d120677c64fe6ca76add81fda36e350605e79';

interface TopNavProps {
  onHistoryClick?: () => void;
  onFaqClick?: () => void;
  onPrizeTiersClick?: () => void;
  onSettingsClick?: () => void;
  onNFTGalleryClick?: () => void;
}

export function TopNav({
  onHistoryClick = () => {},
  onFaqClick = () => {},
  onPrizeTiersClick = () => {},
  onSettingsClick = () => {},
  onNFTGalleryClick = () => {},
}: TopNavProps = {}) {
  const { open } = useAppKit();
  const { address, isConnected } = useWalletAddress();
  const { walletInfo } = useWalletInfo('solana');
  const isPhantomActive = useIsPhantomActive();
  const { connectPhantom, isEmbeddedWallet } = usePhantom();
  const [showMobileWalletSheet, setShowMobileWalletSheet] = useState(false);

  return (
    <>
      <nav
        className="backdrop-blur-sm sticky top-0 z-50"
        style={{
          backgroundColor: '#1B1B1B',
          borderBottom: '2px solid #51E4F8',
          position: 'sticky'
        }}
      >
        <div className="container mx-auto px-4 max-w-full">
          <div className="flex items-center justify-between h-16 min-w-0">
            {/* Logo with Powered by Bonk */}
            <div className="flex items-center space-x-2 min-w-0 flex-shrink-0">
              <a href="/chest" className="cursor-pointer">
                {/* Mobile logo */}
                <img
                  src="/logo.svg"
                  alt="Junk.Fun Logo"
                  className="object-contain md:hidden"
                  style={{ width: 'auto', height: '21px' }}
                />
                {/* Desktop logo */}
                <img
                  src="/logo.svg"
                  alt="Junk.Fun Logo"
                  className="object-contain hidden md:block"
                  style={{ width: 'auto', height: '32px' }}
                />
              </a>
              {/* Mobile Bonk logo */}
              <img
                src="/powered_by_bonk.png"
                alt="Powered by Bonk"
                className="object-contain md:hidden"
                style={{ width: 'auto', height: '25px' }}
              />
              {/* Mobile & symbol */}
              <span
                className="md:hidden text-white font-bold"
                style={{ fontSize: '19px', margin: '0 4px', fontFamily: 'Arial, sans-serif' }}
              >
                &
              </span>
              {/* Mobile Manta logo */}
              <img
                src="/manta.svg"
                alt="Manta"
                className="object-contain md:hidden"
                style={{ width: 'auto', height: '25px' }}
              />
              {/* Desktop Bonk logo */}
              <img
                src="/powered_by_bonk.png"
                alt="Powered by Bonk"
                className="object-contain hidden md:block"
                style={{ width: 'auto', height: '52px' }}
              />
              {/* Desktop & symbol */}
              <span
                className="hidden md:block text-white font-bold"
                style={{ fontSize: '32px', margin: '0 8px', fontFamily: 'Arial, sans-serif' }}
              >
                &
              </span>
              {/* Desktop Manta logo */}
              <img
                src="/manta.svg"
                alt="Manta"
                className="object-contain hidden md:block"
                style={{ width: 'auto', height: '64px' }}
              />
            </div>

            {/* Center Spacer */}
            <div className="flex-1"></div>

            {/* Right Side */}
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-shrink-0">
              {/* New Year Campaign Link */}
              {config.ENABLE_NEW_YEAR_CAMPAIGN && (
                <a
                  href="/new-year-campaign"
                  className="flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-lg hover:opacity-90 transition-all flex-shrink-0 border border-yellow-500/50"
                  style={{
                    fontFamily: 'Tiny5, monospace',
                    background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #F59E0B 100%)',
                    boxShadow: '0 0 12px rgba(251, 191, 36, 0.4)',
                  }}
                >
                  <span className="text-lg">🎉</span>
                  <span className="text-black text-sm font-bold hidden md:inline">NY Campaign</span>
                </a>
              )}

              {/* Docs Link */}
              <a
                href="https://docs.junk.fun/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                style={{ fontFamily: 'Tiny5, monospace' }}
              >
                <img
                  src="/gitbook-icon.svg"
                  alt="Docs"
                  className="w-4 h-4"
                  style={{ filter: 'invert(1)' }}
                />
                <span className="text-white text-sm hidden md:inline">Docs</span>
              </a>

              {/* Campaign Badges - Hidden on mobile */}
              {isConnected && (
                <div className="hidden md:flex flex-shrink-0 gap-2">
                  <SolflareCampaignBadge />
                  <MagicEdenCampaignBadge />
                  <GateCampaignBadge />
                  <ReferralCampaignBadge />
                </div>
              )}

              {/* Points Display - Hidden on mobile */}
              <div className="hidden md:flex flex-shrink-0">
                <PointsDisplay />
              </div>

              {/* User Stats - Hidden on mobile */}
              <div className="hidden md:flex flex-shrink-0">
                <UserStats />
              </div>

              {/* Campaign Promotions - Hidden on mobile, only when not connected */}
              {!isConnected && (
                <div className="hidden md:flex flex-shrink-0 gap-2">
                  <MagicEdenStakingPromotion />
                </div>
              )}

              {/* Wallet Connect */}
              <div className="wallet-adapter flex-shrink-0 relative w-full md:w-auto">
                {isConnected && address ? (
                  <WalletDropdown
                    walletAddress={address}
                    onHistoryClick={onHistoryClick}
                    onFaqClick={onFaqClick}
                    onPrizeTiersClick={onPrizeTiersClick}
                    onSettingsClick={onSettingsClick}
                    onNFTGalleryClick={onNFTGalleryClick}
                    isSolflareWallet={Boolean(
                      String(walletInfo?.name || '').toLowerCase().includes('solflare') ||
                        String(walletInfo?.rdns || '').toLowerCase().includes('solflare') ||
                        walletInfo?.explorerId === SOLFLARE_WALLET_ID
                    )}
                    isPhantomWallet={isPhantomActive}
                    isEmbeddedWallet={isEmbeddedWallet}
                  />
                ) : (
                  <>
                    {/* Mobile: Single Connect button */}
                    <button
                      onClick={() => setShowMobileWalletSheet(true)}
                      className={cn(
                        'md:hidden px-4 py-2 rounded-lg hover:opacity-90 transition-colors',
                        'text-sm font-medium',
                        'h-9 flex items-center justify-center space-x-2'
                      )}
                      style={{
                        fontFamily: 'Tiny5, monospace',
                        backgroundColor: '#50E4F9',
                        color: 'black',
                      }}
                    >
                      <Wallet className="w-4 h-4 flex-shrink-0" style={{ color: 'black' }} />
                      <span>Connect</span>
                    </button>

                    {/* Desktop: Wallet buttons */}
                    <div className="hidden md:flex flex-row items-center gap-2">
                      {config.ENABLE_PHANTOM_EMBEDDED_WALLET && <PhantomConnectButton />}
                      {config.ENABLE_GATE_WALLET_CAMPAIGN && <GateWalletConnectButton />}
                      <button
                        onClick={() => open()}
                        className={cn(
                          'px-3 py-2 rounded-lg hover:opacity-90 transition-colors',
                          'text-sm font-medium',
                          'w-[140px] h-9 flex items-center justify-center space-x-2'
                        )}
                        style={{
                          fontFamily: 'Tiny5, monospace',
                          backgroundColor: '#50E4F9',
                          color: 'black',
                        }}
                      >
                        <Wallet className="w-4 h-4 flex-shrink-0" style={{ color: 'black' }} />
                        <span className="truncate">Connect Wallet</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

        </div>
      </nav>

      {/* Mobile Stats Bar - Only visible on mobile when connected */}
      {isConnected && (
        <div
          className="md:hidden w-full sticky top-[64px] z-40"
          style={{
            backgroundColor: '#262626',
            padding: '2px 8px',
          }}
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-around gap-1">
              <div className="flex-1 scale-[0.65]">
                <PointsDisplay />
              </div>
              <div className="flex-1 scale-[0.65]">
                <UserStats />
              </div>
            </div>
            {/* Campaign Badges on mobile */}
            <div className="flex items-center justify-center gap-2 pb-1">
              <SolflareCampaignBadge />
              <MagicEdenCampaignBadge />
              <GateCampaignBadge />
              <ReferralCampaignBadge />
            </div>
          </div>
        </div>
      )}

      {/* Mobile Wallet Selection Bottom Sheet */}
      {showMobileWalletSheet && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-[100]"
            onClick={() => setShowMobileWalletSheet(false)}
          />
          {/* Sheet */}
          <div
            className="md:hidden fixed bottom-0 left-0 right-0 z-[101] rounded-t-2xl p-4 pb-8 animate-slide-up"
            style={{ backgroundColor: '#1B1B1B', borderTop: '2px solid #51E4F8' }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className="text-lg font-bold text-white"
                style={{ fontFamily: 'Tiny5, monospace' }}
              >
                Connect Wallet
              </h3>
              <button
                onClick={() => setShowMobileWalletSheet(false)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="flex flex-col gap-3">
              {/* Phantom Option - Only shown when enabled (development) */}
              {config.ENABLE_PHANTOM_EMBEDDED_WALLET && (
                <button
                  onClick={() => {
                    setShowMobileWalletSheet(false);
                    connectPhantom();
                  }}
                  className="w-full px-4 py-3 rounded-xl flex items-center gap-3 transition-colors"
                  style={{
                    backgroundColor: '#AB9FF2',
                    border: '1px solid #AB9FF2',
                  }}
                >
                  <img
                    src="/phantom-icon.svg"
                    alt="Phantom"
                    className="w-6 h-6"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <span
                    className="font-medium text-black"
                    style={{ fontFamily: 'Tiny5, monospace' }}
                  >
                    Phantom
                  </span>
                  <span
                    className="ml-auto text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'rgba(0,0,0,0.2)', color: '#000' }}
                  >
                    Embedded
                  </span>
                </button>
              )}

              {/* Gate Wallet Option */}
              {config.ENABLE_GATE_WALLET_CAMPAIGN && (
                <button
                  onClick={() => {
                    setShowMobileWalletSheet(false);

                    // Check if Gate Wallet is installed
                    if (!isGateWalletInstalled()) {
                      // Not installed - redirect to download page
                      window.open(GATE_WALLET_DOWNLOAD_URL, '_blank');
                      return;
                    }

                    // Gate Wallet is installed - open AppKit modal (Gate Wallet is in featuredWalletIds)
                    open();
                  }}
                  className="w-full px-4 py-3 rounded-xl flex items-center gap-3 transition-colors"
                  style={{
                    background: 'linear-gradient(135deg, #0068FF 0%, #17E6A1 100%)',
                    border: '1px solid #17E6A1',
                  }}
                >
                  <img
                    src="/gate-icon.svg"
                    alt="Gate Wallet"
                    className="w-7 h-7"
                  />
                  <span
                    className="font-medium text-white"
                    style={{ fontFamily: 'Tiny5, monospace' }}
                  >
                    Gate Wallet
                  </span>
                </button>
              )}

              {/* Other Wallets Option */}
              <button
                onClick={() => {
                  setShowMobileWalletSheet(false);
                  open();
                }}
                className="w-full px-4 py-3 rounded-xl flex items-center gap-3 transition-colors"
                style={{
                  backgroundColor: '#50E4F9',
                  border: '1px solid #50E4F9',
                }}
              >
                <Wallet className="w-6 h-6" style={{ color: 'black' }} />
                <span
                  className="font-medium text-black"
                  style={{ fontFamily: 'Tiny5, monospace' }}
                >
                  Other Wallets
                </span>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
